import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../services/api';
import { useAuthStore } from '../../../store/authStore';
import { useToast } from '../../../components/ui/use-toast';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Separator } from '../../../components/ui/separator';
import { 
  X, 
  Save, 
  MapPin, 
  DollarSign, 
  FileText, 
  Phone,
  User,
  Mail,
  Building,
  CalendarDays,
  Settings,
  Star,
  Edit2,
  CheckCircle,
  XCircle,
  Clock,
  FileCheck,
  FileArchive
} from 'lucide-react';
import { buildMonthOptions, currentMonthValue, monthToPeriod } from '../../../utils/monthPeriod';
import { downloadCompletionDocumentsZip } from '../../../utils/completionDocuments';

const RECONCILIATION_LABELS = {
  approved: { label: 'Mutabıkız', color: 'bg-green-100 text-green-700 border-green-200' },
  rejected: { label: 'Mutabık Değiliz', color: 'bg-red-100 text-red-700 border-red-200' },
  pending: { label: 'Bekliyor', color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
};

const OrganizationPricingModal = ({ organization, isOpen, onClose, onSuccess }) => {
  const { user } = useAuthStore();
  const isExternalInstitution = user?.role === 'external_institution';
  const isParentInstitution = user?.role === 'external_parent_institution';
  const canReconcile = ['super_admin', 'admin', 'external_institution', 'external_parent_institution'].includes(user?.role);
  // Çatı kurum kullanıcısı: önce alt kurum özetleri, tıklayınca detay
  const [selectedChildId, setSelectedChildId] = useState(null);

  // Kurum-spesifik bölgeler
  const [zones, setZones] = useState([]);
  
  const [pricing, setPricing] = useState([]);
  const [settings, setSettings] = useState({
    free_case_limit: 0,
    free_case_period: 'monthly',
    contract_fixed_amount: 0,
    payment_terms: '',
    special_conditions: '',
    billing_address: '',
    emergency_contact: '',
    contract_notes: '',
    is_vip: 0,
    parent_institution_id: null
  });
  const [orgData, setOrgData] = useState({
    contact_person: '',
    contact_phone: '',
    contact_email: '',
    address: '',
    tax_number: '',
    contract_start_date: '',
    contract_end_date: ''
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState((isExternalInstitution || isParentInstitution) ? 'reconciliation' : 'pricing');
  const [allInstitutions, setAllInstitutions] = useState([]);
  const [editingZone, setEditingZone] = useState(null);
  const [tempZoneData, setTempZoneData] = useState({});
  const [scrollPosition, setScrollPosition] = useState(0);

  // Mutabakat state'leri
  const [selectedCaseIds, setSelectedCaseIds] = useState([]);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectNote, setRejectNote] = useState('');
  const [reconcStartDate, setReconcStartDate] = useState('');
  const [reconcEndDate, setReconcEndDate] = useState('');
  const [reconcStatusFilter, setReconcStatusFilter] = useState('all');
  const [caseStatusFilter, setCaseStatusFilter] = useState([]);

  // Mutabakat e-posta bildirimi state'leri (hastanelerdeki sistemin aynısı)
  const canSendEmail = ['super_admin', 'admin'].includes(user?.role);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailType, setEmailType] = useState('reminder'); // 'reminder' | 'reconciliation_complete'
  const [emailMonth, setEmailMonth] = useState(() => currentMonthValue()); // "YYYY-MM"
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [isDownloadingDocs, setIsDownloadingDocs] = useState(false);
  const monthOptions = React.useMemo(() => buildMonthOptions(18), []);
  const emailPeriod = monthToPeriod(emailMonth);

  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Mutabakat için vaka sorgusu (external_institution)
  const {
    data: reconcCasesData,
    isLoading: reconcLoading,
    refetch: refetchReconcCases
  } = useQuery({
    queryKey: ['institution-cases', organization?.id, reconcStartDate, reconcEndDate],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (reconcStartDate) params.append('startDate', reconcStartDate);
      if (reconcEndDate) params.append('endDate', reconcEndDate);
      const res = await api.get(`/institutions/${organization.id}/cases?${params}`);
      return res.data.data || [];
    },
    enabled: isOpen && !!organization?.id && activeTab === 'reconciliation' && !isParentInstitution,
    staleTime: 30000
  });

  // Çatı kurum için parent-cases sorgusu
  const {
    data: parentCasesData,
    isLoading: parentCasesLoading,
    refetch: refetchParentCases
  } = useQuery({
    queryKey: ['parent-institution-cases', organization?.id, reconcStartDate, reconcEndDate, selectedChildId],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (reconcStartDate) params.append('startDate', reconcStartDate);
      if (reconcEndDate) params.append('endDate', reconcEndDate);
      if (selectedChildId) params.append('child_id', selectedChildId);
      const res = await api.get(`/institutions/${organization.id}/parent-cases?${params}`);
      return res.data;
    },
    enabled: isOpen && !!organization?.id && activeTab === 'reconciliation' && isParentInstitution,
    staleTime: 30000
  });

  const institutionCases = isParentInstitution
    ? (parentCasesData?.data || [])
    : (reconcCasesData || []);
  const childSummaries = parentCasesData?.childSummaries || [];

  const reconcFilteredCases = reconcStatusFilter === 'all'
    ? institutionCases
    : reconcStatusFilter === 'pending'
      ? institutionCases.filter(c => !c.reconciliation_status || c.reconciliation_status === 'pending')
      : institutionCases.filter(c => c.reconciliation_status === reconcStatusFilter);

  const filteredCases = caseStatusFilter.length === 0
    ? reconcFilteredCases
    : reconcFilteredCases.filter(c => caseStatusFilter.includes(c.status));

  const toggleCaseStatusFilter = (status) => {
    setCaseStatusFilter(prev =>
      prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]
    );
    setSelectedCaseIds([]);
  };

  const caseStatusCounts = {
    initiated: institutionCases.filter(c => c.status === 'initiated').length,
    in_progress: institutionCases.filter(c => c.status === 'in_progress').length,
    completed: institutionCases.filter(c => c.status === 'completed').length,
    cancelled: institutionCases.filter(c => c.status === 'cancelled').length,
  };

  // Mutabakat mutasyonu
  const reconciliationMutation = useMutation({
    mutationFn: (payload) => api.patch('/cases/reconciliation', payload),
    onSuccess: (res, variables) => {
      queryClient.invalidateQueries(['institution-cases']);
      setSelectedCaseIds([]);
      const label = variables.status === 'approved' ? 'Mutabıkız' : 'Mutabık Değiliz';
      toast({ title: 'Başarılı', description: `${res.data?.updated_count || 0} vaka "${label}" olarak işaretlendi` });
    },
    onError: (error) => {
      toast({ title: 'Hata', description: error.response?.data?.error || 'Mutabakat güncellenemedi', variant: 'destructive' });
    }
  });

  const toggleCaseSelect = (caseId) => {
    setSelectedCaseIds(prev =>
      prev.includes(caseId) ? prev.filter(id => id !== caseId) : [...prev, caseId]
    );
  };

  const toggleSelectAll = () => {
    const visibleIds = filteredCases.map(c => c.id);
    const allSelected = visibleIds.length > 0 && visibleIds.every(id => selectedCaseIds.includes(id));
    if (allSelected) {
      setSelectedCaseIds(prev => prev.filter(id => !visibleIds.includes(id)));
    } else {
      setSelectedCaseIds(prev => [...new Set([...prev, ...visibleIds])]);
    }
  };

  const handleApprove = () => {
    if (selectedCaseIds.length === 0) {
      toast({ title: 'Uyarı', description: 'Lütfen en az bir vaka seçin', variant: 'destructive' });
      return;
    }
    reconciliationMutation.mutate({ case_ids: selectedCaseIds, status: 'approved' });
  };

  const handleRejectOpen = () => {
    if (selectedCaseIds.length === 0) {
      toast({ title: 'Uyarı', description: 'Lütfen en az bir vaka seçin', variant: 'destructive' });
      return;
    }
    setShowRejectModal(true);
  };

  const handleRejectConfirm = () => {
    if (!rejectNote.trim()) {
      toast({ title: 'Hata', description: '"Mutabık Değiliz" için gerekçe açıklaması zorunludur', variant: 'destructive' });
      return;
    }
    reconciliationMutation.mutate({ case_ids: selectedCaseIds, status: 'rejected', note: rejectNote.trim() });
    setShowRejectModal(false);
    setRejectNote('');
  };

  // Kuruma mutabakat e-postası gönder (onay bildiriminde seçilen ayın Excel'i ek olarak gider)
  const emailRecipient = orgData.contact_email || organization?.contact_email || '';
  const emailRecipientName = orgData.contact_person || organization?.contact_person || '';

  const handleSendEmail = async () => {
    if (!organization?.id) return;
    if (!emailRecipient) {
      toast({ title: 'Uyarı', description: 'Bu kurum için kayıtlı e-posta adresi yok. Önce "İletişim Bilgileri" sekmesinden ekleyin.', variant: 'destructive' });
      return;
    }
    setIsSendingEmail(true);
    try {
      const res = await api.post(`/institutions/${organization.id}/send-email`, {
        type: emailType,
        period_start: emailPeriod?.startDate || null,
        period_end: emailPeriod?.endDate || null,
      });
      toast({ title: 'Başarılı', description: res.data?.message || 'E-posta gönderildi' });
      setShowEmailModal(false);
    } catch (err) {
      toast({ title: 'Hata', description: err.response?.data?.error || 'E-posta gönderilemedi', variant: 'destructive' });
    } finally {
      setIsSendingEmail(false);
    }
  };

  const getReconciliationBadge = (c) => {
    const st = c.reconciliation_status;
    if (!st || st === 'pending') return RECONCILIATION_LABELS.pending;
    return RECONCILIATION_LABELS[st] || RECONCILIATION_LABELS.pending;
  };

  const reconcSummary = {
    total: institutionCases.length,
    approved: institutionCases.filter(c => c.reconciliation_status === 'approved').length,
    rejected: institutionCases.filter(c => c.reconciliation_status === 'rejected').length,
    pending: institutionCases.filter(c => !c.reconciliation_status || c.reconciliation_status === 'pending').length,
  };

  useEffect(() => {
    if (isOpen && organization) {
      fetchData();
      setScrollPosition(0);
      setSelectedCaseIds([]);
      setSelectedChildId(null);
      setActiveTab((isExternalInstitution || isParentInstitution) ? 'reconciliation' : 'pricing');
      setCaseStatusFilter([]);
    }
  }, [isOpen, organization]);

  // Çatı kurum listesi için kurumları yükle (sadece admin/super_admin)
  useEffect(() => {
    if (isOpen && !isExternalInstitution && !isParentInstitution) {
      api.get('/institutions?mode=pricing_summary').then(res => {
        if (res.data.success) {
          setAllInstitutions(res.data.data || []);
        }
      }).catch(() => {});
    }
  }, [isOpen]);

  // Preserve scroll position when changing tabs
  const handleTabChange = (newTab) => {
    const modalContent = document.querySelector('.modal-content');
    if (modalContent) {
      setScrollPosition(modalContent.scrollTop);
    }
    setActiveTab(newTab);
    
    // Restore scroll position after a brief delay
    setTimeout(() => {
      if (modalContent) {
        modalContent.scrollTop = scrollPosition;
      }
    }, 100);
  };

  const fetchData = async () => {
    // external_institution ve external_parent_institution sadece mutabakat sekmesini görür
    if (isExternalInstitution || isParentInstitution) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      // Paralel olarak hem org data hem de kurum-spesifik bölgeleri al
      const [orgResponse, zonesResponse] = await Promise.all([
        api.get(`/institutions/${organization.id}`),
        api.get(`/institutions/${organization.id}/zones`)
      ]);
      
      if (orgResponse.data.success && zonesResponse.data.success) {
        const data = orgResponse.data.data;
        const zonesData = zonesResponse.data.data;
        
        // Kurum-spesifik bölgeleri set et
        setZones(zonesData);
        
        // Pricing data'yı backend'den gelen zones data'sından al
        const pricingData = zonesData.map(zone => ({
          zone_id: zone.zone_id,
          zone_number: zone.zone_number,
          zone_name: zone.zone_name,
          base_price: zone.base_price || 0,
          doctor_request_price: data.doctor_request_price || 0,
          nurse_request_price: 0, // Backend'de yok ama frontend bekliyor
          paramedic_hourly_price: data.paramedic_hourly_price || 0,
          additional_service_price: data.additional_service_price || 0,
          locations: zone.locations || '',
          custom_zone_name: zone.zone_name
        }));
        
        setPricing(pricingData);
        
        // Settings'i ayarla
        setSettings({
          free_case_limit: data.free_case_limit || 0,
          free_case_period: data.free_case_period || 'monthly',
          contract_fixed_amount: data.contract_fixed_amount || 0,
          payment_terms: data.payment_terms || '',
          special_conditions: data.special_conditions || '',
          billing_address: data.billing_address || '',
          emergency_contact: data.emergency_contact || '',
          contract_notes: data.contract_notes || '',
          is_vip: data.is_vip || 0,
          parent_institution_id: data.parent_institution_id || null
        });
        
        // Org data'yı ayarla
        setOrgData({
          contact_person: data.contact_person || '',
          contact_phone: data.contact_phone || '',
          contact_email: data.contact_email || '',
          address: data.address || '',
          tax_number: data.tax_number || '',
          contract_start_date: data.contract_start_date || '',
          contract_end_date: data.contract_end_date || ''
        });
        
      } else {
        throw new Error(response.data.message || 'Veri yüklenemedi');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        variant: "destructive",
        title: "Hata",
        description: `Veriler yüklenirken hata oluştu: ${error.message}`,
        duration: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePricingChange = (zoneId, field, value) => {
    setPricing(prev => prev.map(p => 
      p.zone_id === zoneId 
        ? { ...p, [field]: parseFloat(value) || 0 }
        : p
    ));
  };

  const handleSettingsChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const getZoneDisplayName = (zonePricing) => {
    const zoneName = zonePricing.custom_zone_name || zonePricing.zone_name;
    const baseName = `${zonePricing.zone_number}. Bölge - ${zoneName}`;
    if (zonePricing.locations && zonePricing.locations.trim()) {
      return `${baseName} (${zonePricing.locations})`;
    }
    return baseName;
  };

  const startEditZone = (zone) => {
    setEditingZone(zone.zone_id);
    setTempZoneData({
      zone_name: zone.custom_zone_name || zone.zone_name || '',
      locations: zone.locations || ''
    });
  };

  const cancelEditZone = () => {
    setEditingZone(null);
    setTempZoneData({});
  };

  const saveZone = async (zoneId) => {
    try {
      setSaving(true);
      
      // Backend'e zone verilerini gönder (name, locations)
      const response = await api.post(`/institutions/${organization.id}/zones/${zoneId}/locations`, {
        locations: tempZoneData.locations,
        zone_name: tempZoneData.zone_name
      });
      
      toast({
        title: "Başarılı",
        description: "Bölge bilgileri başarıyla güncellendi"
      });
      
      setEditingZone(null);
      setTempZoneData({});

      // Pricing'i güncelle - tüm alanları güncelle
      setPricing(prev => prev.map(p => 
        p.zone_id === zoneId 
          ? { 
              ...p, 
              locations: tempZoneData.locations,
              custom_zone_name: tempZoneData.zone_name,
              zone_name: tempZoneData.zone_name || `${p.zone_number}. Bölge`
            }
          : p
      ));

      // DON'T refresh parent component immediately to avoid tab reset
      // The parent will get updated when modal closes or when user navigates away
      
    } catch (error) {
      console.error('Error saving organization zone:', error);
      toast({
        variant: "destructive",
        title: "Hata",
        description: error.response?.data?.message || "Bölge bilgileri kaydedilirken hata oluştu"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      // Fiyatlandırma kaydet - basit API
      const zones = pricing.map(p => ({
        zone_id: p.zone_id,
        base_price: p.base_price,
        locations: p.locations || ''
      }));

      await api.post(`/institutions/${organization.id}/pricing`, {
        zones,
        doctor_request_price: pricing[0]?.doctor_request_price || 0,
        paramedic_hourly_price: pricing[0]?.paramedic_hourly_price || 0,
        additional_service_price: pricing[0]?.additional_service_price || 0
      });

      // Settings kaydet - basit API
      await api.post(`/institutions/${organization.id}/settings`, {
        ...settings,
        ...orgData // Org data'yı da birlikte gönder
      });

      toast({
        title: "Başarılı",
        description: "Fiyatlandırma ve ayarlar başarıyla kaydedildi"
      });

      // Refresh parent component when modal closes
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (error) {
      console.error('Error saving data:', error);
      toast({
        variant: "destructive",
        title: "Hata",
        description: "Kaydetme işlemi sırasında hata oluştu"
      });
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  // Safety check for organization data
  if (!organization || !organization.id) {
    console.error('OrganizationPricingModal: Invalid organization data', organization);
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white/95 backdrop-blur-xl rounded-3xl max-w-6xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-white/20 modal-content">
        {/* Modern Header */}
        <div className="sticky top-0 z-50 bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 rounded-t-3xl px-8 py-6 backdrop-blur-xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-white/20 backdrop-blur-sm rounded-2xl">
                <Building className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">
                  {organization.name}
                </h2>
                <p className="text-white/80">
                  Fiyatlandırma ve Anlaşma Ayarları
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {!isExternalInstitution && (
                <Button 
                  variant="ghost"
                  size="sm"
                  onClick={() => handleTabChange('zones')}
                  className="bg-white/10 hover:bg-white/20 text-white border border-white/20 rounded-xl px-4 py-2"
                >
                  <MapPin className="w-4 h-4 mr-2" />
                  Bölge Yönetimi
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="bg-white/10 hover:bg-white/20 text-white rounded-2xl p-3"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <>
            {/* Modern Tab Navigation */}
            <div className="sticky top-[104px] z-40 flex flex-wrap gap-2 px-8 py-4 bg-gray-50/90 backdrop-blur-xl border-b border-gray-200/50">
              {!isExternalInstitution && !isParentInstitution && (
                <>
                  <button
                    onClick={() => handleTabChange('pricing')}
                    className={`flex items-center px-6 py-3 rounded-2xl font-medium transition-all duration-300 ${
                      activeTab === 'pricing'
                        ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                        : 'bg-white/60 text-gray-600 hover:bg-white/80 hover:text-gray-900'
                    }`}
                  >
                    <DollarSign className="w-5 h-5 mr-2" />
                    Bölgesel Fiyatlandırma
                  </button>
                  <button
                    onClick={() => handleTabChange('settings')}
                    className={`flex items-center px-6 py-3 rounded-2xl font-medium transition-all duration-300 ${
                      activeTab === 'settings'
                        ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                        : 'bg-white/60 text-gray-600 hover:bg-white/80 hover:text-gray-900'
                    }`}
                  >
                    <Settings className="w-5 h-5 mr-2" />
                    Anlaşma Ayarları
                  </button>
                  <button
                    onClick={() => handleTabChange('contact')}
                    className={`flex items-center px-6 py-3 rounded-2xl font-medium transition-all duration-300 ${
                      activeTab === 'contact'
                        ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                        : 'bg-white/60 text-gray-600 hover:bg-white/80 hover:text-gray-900'
                    }`}
                  >
                    <User className="w-5 h-5 mr-2" />
                    İletişim Bilgileri
                  </button>
                  <button
                    onClick={() => handleTabChange('zones')}
                    className={`flex items-center px-6 py-3 rounded-2xl font-medium transition-all duration-300 ${
                      activeTab === 'zones'
                        ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                        : 'bg-white/60 text-gray-600 hover:bg-white/80 hover:text-gray-900'
                    }`}
                  >
                    <MapPin className="w-5 h-5 mr-2" />
                    Bölge Yönetimi
                  </button>
                </>
              )}
              <button
                onClick={() => handleTabChange('reconciliation')}
                className={`flex items-center px-6 py-3 rounded-2xl font-medium transition-all duration-300 ${
                  activeTab === 'reconciliation'
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                    : 'bg-white/60 text-gray-600 hover:bg-white/80 hover:text-gray-900'
                }`}
              >
                <FileCheck className="w-5 h-5 mr-2" />
                Vaka Mutabakatı
              </button>
            </div>

            <div className="p-8 relative z-10">
              {/* Pricing Tab */}
              {activeTab === 'pricing' && (
                <div className="space-y-8">
                  <div>
                    <div className="flex items-center space-x-3 mb-6">
                      <div className="p-3 bg-blue-100 rounded-2xl">
                        <DollarSign className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900">Bölgesel Fiyatlandırma</h3>
                        <p className="text-gray-600">Her bölge için farklı hizmet fiyatları belirleyin</p>
                      </div>
                    </div>
                    <div className="grid gap-6">
                      {pricing.map((zonePricing) => (
                        <div 
                          key={zonePricing.zone_id}
                          className="bg-white/60 backdrop-blur-sm rounded-3xl p-6 shadow-lg border border-white/20 hover:shadow-xl transition-all duration-300"
                        >
                          <div className="flex items-center space-x-4 mb-6">
                            <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl">
                              <MapPin className="w-6 h-6 text-white" />
                            </div>
                            <div>
                              <h4 className="text-xl font-bold text-gray-900">
                                {getZoneDisplayName(zonePricing)}
                              </h4>
                              <p className="text-gray-600">Hizmet türü bazlı fiyatlandırma</p>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                            <div className="space-y-2">
                              <div className="flex items-center space-x-2 mb-3">
                                <div className="p-2 bg-green-100 rounded-lg">
                                  <DollarSign className="w-4 h-4 text-green-600" />
                                </div>
                                <Label htmlFor={`base_${zonePricing.zone_id}`} className="font-medium text-gray-700">
                                  Temel Fiyat (₺)
                                </Label>
                              </div>
                              <Input
                                id={`base_${zonePricing.zone_id}`}
                                type="number"
                                step="0.01"
                                value={zonePricing.base_price}
                                onChange={(e) => handlePricingChange(zonePricing.zone_id, 'base_price', e.target.value)}
                                placeholder="0.00"
                                className="rounded-2xl border-0 bg-green-50 focus:ring-2 focus:ring-green-500 text-lg font-semibold"
                              />
                            </div>
                            
                            <div className="space-y-2">
                              <div className="flex items-center space-x-2 mb-3">
                                <div className="p-2 bg-blue-100 rounded-lg">
                                  <User className="w-4 h-4 text-blue-600" />
                                </div>
                                <Label htmlFor={`doctor_${zonePricing.zone_id}`} className="font-medium text-gray-700">
                                  Doktor Talebi (₺)
                                </Label>
                              </div>
                              <Input
                                id={`doctor_${zonePricing.zone_id}`}
                                type="number"
                                step="0.01"
                                value={zonePricing.doctor_request_price}
                                onChange={(e) => handlePricingChange(zonePricing.zone_id, 'doctor_request_price', e.target.value)}
                                placeholder="0.00"
                                className="rounded-2xl border-0 bg-blue-50 focus:ring-2 focus:ring-blue-500 text-lg font-semibold"
                              />
                            </div>
                            
                            <div className="space-y-2">
                              <div className="flex items-center space-x-2 mb-3">
                                <div className="p-2 bg-purple-100 rounded-lg">
                                  <User className="w-4 h-4 text-purple-600" />
                                </div>
                                <Label htmlFor={`paramedic_${zonePricing.zone_id}`} className="font-medium text-gray-700">
                                  Paramedik Talebi (Saatlik) (₺)
                                </Label>
                              </div>
                              <Input
                                id={`paramedic_${zonePricing.zone_id}`}
                                type="number"
                                step="0.01"
                                value={zonePricing.paramedic_hourly_price}
                                onChange={(e) => handlePricingChange(zonePricing.zone_id, 'paramedic_hourly_price', e.target.value)}
                                placeholder="0.00"
                                className="rounded-2xl border-0 bg-purple-50 focus:ring-2 focus:ring-purple-500 text-lg font-semibold"
                              />
                            </div>
                            
                            <div className="space-y-2">
                              <div className="flex items-center space-x-2 mb-3">
                                <div className="p-2 bg-orange-100 rounded-lg">
                                  <Star className="w-4 h-4 text-orange-600" />
                                </div>
                                <Label htmlFor={`additional_${zonePricing.zone_id}`} className="font-medium text-gray-700">
                                  Ek Hizmet (₺)
                                </Label>
                              </div>
                              <Input
                                id={`additional_${zonePricing.zone_id}`}
                                type="number"
                                step="0.01"
                                value={zonePricing.additional_service_price}
                                onChange={(e) => handlePricingChange(zonePricing.zone_id, 'additional_service_price', e.target.value)}
                                placeholder="0.00"
                                className="rounded-2xl border-0 bg-orange-50 focus:ring-2 focus:ring-orange-500 text-lg font-semibold"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Settings Tab */}
              {activeTab === 'settings' && (
                <div className="space-y-6">
                  <div>
                    <div className="flex items-center space-x-3 mb-6">
                      <div className="p-3 bg-yellow-100 rounded-2xl">
                        <Settings className="w-6 h-6 text-yellow-600" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900">Anlaşma Ayarları</h3>
                        <p className="text-gray-600">Sözleşme koşulları ve özel düzenlemeler</p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Free Cases */}
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-base">
                            <FileText className="w-4 h-4 inline mr-2" />
                            Ücretsiz Vakalar
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div>
                            <Label htmlFor="free_case_limit">Ücretsiz Vaka Limiti</Label>
                            <Input
                              id="free_case_limit"
                              type="number"
                              value={settings.free_case_limit}
                              onChange={(e) => handleSettingsChange('free_case_limit', parseInt(e.target.value) || 0)}
                              placeholder="0"
                            />
                          </div>
                          <div>
                            <Label htmlFor="free_case_period">Dönem</Label>
                            <Select
                              value={settings.free_case_period} 
                              onValueChange={(value) => handleSettingsChange('free_case_period', value)}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="monthly">Aylık</SelectItem>
                                <SelectItem value="yearly">Yıllık</SelectItem>
                                <SelectItem value="total">Toplam</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label htmlFor="contract_fixed_amount">Toplam Anlaşma Tutarı (KDV Hariç)</Label>
                            <Input
                              id="contract_fixed_amount"
                              type="number"
                              step="0.01"
                              value={settings.contract_fixed_amount}
                              onChange={(e) => handleSettingsChange('contract_fixed_amount', parseFloat(e.target.value) || 0)}
                              placeholder="0.00"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Ücretsiz vaka kotası için toplam sözleşme tutarı
                            </p>
                          </div>
                        </CardContent>
                      </Card>

                      {/* Emergency Contact */}
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-base">
                            <Phone className="w-4 h-4 inline mr-2" />
                            Acil Durum İletişim
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div>
                            <Label htmlFor="emergency_contact">Acil Durum İletişim</Label>
                            <Textarea
                              id="emergency_contact"
                              value={settings.emergency_contact}
                              onChange={(e) => handleSettingsChange('emergency_contact', e.target.value)}
                              placeholder="7/24 acil durum iletişim bilgileri"
                              rows={3}
                            />
                          </div>
                          <div>
                            <Label htmlFor="is_vip">VIP Kurum</Label>
                            <Select 
                              value={settings.is_vip ? '1' : '0'} 
                              onValueChange={(value) => handleSettingsChange('is_vip', parseInt(value))}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="0">Normal</SelectItem>
                                <SelectItem value="1">VIP</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label htmlFor="parent_institution_id">Çatı Kurum Bağlantısı</Label>
                            <Select
                              value={settings.parent_institution_id ? String(settings.parent_institution_id) : 'none'}
                              onValueChange={(value) => handleSettingsChange('parent_institution_id', value === 'none' ? null : parseInt(value))}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Çatı kurum seçin (opsiyonel)" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">— Bağlı değil —</SelectItem>
                                {allInstitutions
                                  .filter(inst => inst.id !== organization?.id && !inst.parent_institution_id)
                                  .map(inst => (
                                    <SelectItem key={inst.id} value={String(inst.id)}>{inst.name}</SelectItem>
                                  ))}
                              </SelectContent>
                            </Select>
                            <p className="text-xs text-gray-500 mt-1">Bu kurumu bir çatı kuruma bağlamak için seçin</p>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                    
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="payment_terms">Ödeme Koşulları</Label>
                        <Textarea
                          id="payment_terms"
                          value={settings.payment_terms}
                          onChange={(e) => handleSettingsChange('payment_terms', e.target.value)}
                          placeholder="Ödeme koşulları (örn: 30 gün vadeli, peşin ödeme %5 indirim)"
                          rows={3}
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="special_conditions">Özel Koşullar</Label>
                        <Textarea
                          id="special_conditions"
                          value={settings.special_conditions}
                          onChange={(e) => handleSettingsChange('special_conditions', e.target.value)}
                          placeholder="Özel koşullar ve notlar"
                          rows={3}
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="billing_address">Fatura Adresi</Label>
                        <Textarea
                          id="billing_address"
                          value={settings.billing_address}
                          onChange={(e) => handleSettingsChange('billing_address', e.target.value)}
                          placeholder="Fatura adresi"
                          rows={3}
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="contract_notes">Sözleşme Notları</Label>
                        <Textarea
                          id="contract_notes"
                          value={settings.contract_notes}
                          onChange={(e) => handleSettingsChange('contract_notes', e.target.value)}
                          placeholder="Sözleşme ile ilgili ek notlar"
                          rows={3}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Contact Tab */}
              {activeTab === 'contact' && (
                <div className="space-y-6">
                  <div>
                    <div className="flex items-center space-x-3 mb-6">
                      <div className="p-3 bg-green-100 rounded-2xl">
                        <User className="w-6 h-6 text-green-600" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900">İletişim Bilgileri</h3>
                        <p className="text-gray-600">Kurum yetkilileri ve iletişim detayları</p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="contact_person">İletişim Kişisi</Label>
                        <Input
                          id="contact_person"
                          value={orgData.contact_person}
                          onChange={(e) => setOrgData(prev => ({...prev, contact_person: e.target.value}))}
                          placeholder="İletişim kişisi adı"
                        />
                      </div>
                      <div>
                        <Label htmlFor="contact_phone">Telefon</Label>
                        <Input
                          id="contact_phone"
                          value={orgData.contact_phone}
                          onChange={(e) => setOrgData(prev => ({...prev, contact_phone: e.target.value}))}
                          placeholder="Telefon numarası"
                        />
                      </div>
                      <div>
                        <Label htmlFor="contact_email">E-posta</Label>
                        <Input
                          id="contact_email"
                          type="email"
                          value={orgData.contact_email}
                          onChange={(e) => setOrgData(prev => ({...prev, contact_email: e.target.value}))}
                          placeholder="E-posta adresi"
                        />
                      </div>
                      <div>
                        <Label htmlFor="tax_number">Vergi Numarası</Label>
                        <Input
                          id="tax_number"
                          value={orgData.tax_number}
                          onChange={(e) => setOrgData(prev => ({...prev, tax_number: e.target.value}))}
                          placeholder="Vergi numarası"
                        />
                      </div>
                      <div>
                        <Label htmlFor="address">Adres</Label>
                        <Textarea
                          id="address"
                          value={orgData.address}
                          onChange={(e) => setOrgData(prev => ({...prev, address: e.target.value}))}
                          placeholder="Adres"
                          rows={3}
                        />
                      </div>
                      <div className="space-y-4">
                        <div>
                          <Label htmlFor="contract_start_date">Sözleşme Başlangıç</Label>
                          <Input
                            id="contract_start_date"
                            type="date"
                            value={orgData.contract_start_date}
                            onChange={(e) => setOrgData(prev => ({...prev, contract_start_date: e.target.value}))}
                          />
                        </div>
                        <div>
                          <Label htmlFor="contract_end_date">Sözleşme Bitiş</Label>
                          <Input
                            id="contract_end_date"
                            type="date"
                            value={orgData.contract_end_date}
                            onChange={(e) => setOrgData(prev => ({...prev, contract_end_date: e.target.value}))}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Zones Tab */}
              {activeTab === 'zones' && (
                <div className="space-y-6">
                  <div>
                    <div className="flex items-center space-x-3 mb-6">
                      <div className="p-3 bg-purple-100 rounded-2xl">
                        <MapPin className="w-6 h-6 text-purple-600" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900">Bölge Yönetimi</h3>
                        <p className="text-gray-600">Bölge bilgilerini ve yerlerini düzenleyin</p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {pricing.map((zone) => (
                        <div key={zone.zone_id} className="bg-white/60 backdrop-blur-sm rounded-3xl p-6 shadow-lg border border-white/20 hover:shadow-xl transition-all duration-300">
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center space-x-3">
                              <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl">
                                <MapPin className="w-5 h-5 text-white" />
                              </div>
                              <div>
                                <h4 className="text-lg font-bold text-gray-900">
                                  {zone.custom_zone_name || zone.zone_name}
                                </h4>
                                <span className="text-sm text-gray-500">Bölge {zone.zone_number}</span>
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => startEditZone(zone)}
                              disabled={editingZone === zone.zone_id}
                              className="rounded-xl"
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                          </div>
                          
                          {editingZone === zone.zone_id ? (
                            <div className="space-y-4">
                              <div>
                                <Label htmlFor={`zone-name-${zone.zone_id}`} className="text-sm font-medium text-gray-700">
                                  Bölge Adı
                                </Label>
                                <Input
                                  id={`zone-name-${zone.zone_id}`}
                                  value={tempZoneData.zone_name}
                                  onChange={(e) => setTempZoneData(prev => ({ ...prev, zone_name: e.target.value }))}
                                  className="rounded-xl mt-1"
                                  placeholder="Bölge adı"
                                />
                              </div>
                              

                              
                              <div>
                                <Label htmlFor={`zone-locations-${zone.zone_id}`} className="text-sm font-medium text-gray-700">
                                  Yerler <span className="text-gray-500">(virgülle ayırın)</span>
                                </Label>
                                <Textarea
                                  id={`zone-locations-${zone.zone_id}`}
                                  value={tempZoneData.locations}
                                  onChange={(e) => setTempZoneData(prev => ({ ...prev, locations: e.target.value }))}
                                  className="rounded-xl mt-1"
                                  placeholder="Erenköy, Kozyatağı, Yenisahra"
                                  rows={3}
                                />
                              </div>
                              
                              <div className="flex space-x-2">
                                <Button
                                  onClick={() => saveZone(zone.zone_id)}
                                  disabled={saving}
                                  className="flex-1 rounded-xl bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800"
                                >
                                  {saving ? (
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                  ) : (
                                    <Save className="w-4 h-4 mr-2" />
                                  )}
                                  Kaydet
                                </Button>
                                <Button
                                  variant="outline"
                                  onClick={cancelEditZone}
                                  disabled={saving}
                                  className="rounded-xl"
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <div className="space-y-3">

                              
                              <div>
                                <span className="text-sm font-medium text-gray-500">Yerler:</span>
                                {zone.locations ? (
                                  <div className="flex flex-wrap gap-2 mt-2">
                                    {zone.locations.split(',').map((location, index) => (
                                      <Badge key={index} variant="outline" className="text-xs">
                                        {location.trim()}
                                      </Badge>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-gray-500 text-sm mt-1">Henüz yer eklenmemiş</p>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Reconciliation Tab */}
              {activeTab === 'reconciliation' && (
                <div className="space-y-6">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="p-3 bg-indigo-100 rounded-2xl">
                      <FileCheck className="w-6 h-6 text-indigo-600" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900">Vaka Mutabakatı</h3>
                      <p className="text-gray-600">
                        {isParentInstitution && !selectedChildId
                          ? 'Alt kurum özet kartlarına tıklayarak detaya gidin'
                          : 'Kuruma ait vakalar için mutabakat onayı'}
                      </p>
                    </div>
                    {isParentInstitution && selectedChildId && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="ml-auto rounded-xl"
                        onClick={() => { setSelectedChildId(null); setSelectedCaseIds([]); }}
                      >
                        ← Özete Dön
                      </Button>
                    )}
                  </div>

                  {/* Çatı kurum: Alt kurum özet kartları (childId seçili değilse) */}
                  {isParentInstitution && !selectedChildId && (
                    parentCasesLoading ? (
                      <div className="flex items-center justify-center p-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {/* Tarih filtresi */}
                        <div className="flex flex-wrap gap-3 items-end bg-white/60 rounded-2xl p-4 border border-gray-200">
                          <div>
                            <Label className="text-xs text-gray-600 mb-1 block">Başlangıç Tarihi</Label>
                            <Input type="date" value={reconcStartDate} onChange={(e) => setReconcStartDate(e.target.value)} className="rounded-xl text-sm" />
                          </div>
                          <div>
                            <Label className="text-xs text-gray-600 mb-1 block">Bitiş Tarihi</Label>
                            <Input type="date" value={reconcEndDate} onChange={(e) => setReconcEndDate(e.target.value)} className="rounded-xl text-sm" />
                          </div>
                        </div>
                        {/* Alt kurum kartları */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                          {childSummaries.length === 0 ? (
                            <div className="col-span-3 text-center text-gray-500 py-8">Alt kurum bulunamadı</div>
                          ) : (
                            childSummaries.map(child => (
                              <div
                                key={child.id}
                                className="bg-white rounded-2xl p-5 border border-gray-200 shadow-sm hover:shadow-md cursor-pointer transition-all hover:border-indigo-300"
                                onClick={() => setSelectedChildId(child.id)}
                              >
                                <div className="flex items-center justify-between mb-3">
                                  <h4 className="font-semibold text-gray-900 text-sm leading-tight">{child.name}</h4>
                                  <span className="text-xs text-indigo-600 font-medium bg-indigo-50 px-2 py-0.5 rounded-lg">Detay →</span>
                                </div>
                                <div className="grid grid-cols-3 gap-2 text-center">
                                  <div>
                                    <div className="text-lg font-bold text-gray-800">{child.total}</div>
                                    <div className="text-xs text-gray-500">Toplam</div>
                                  </div>
                                  <div>
                                    <div className="text-lg font-bold text-green-600">{child.approved}</div>
                                    <div className="text-xs text-green-500">Mutabıkız</div>
                                  </div>
                                  <div>
                                    <div className="text-lg font-bold text-yellow-600">{child.pending}</div>
                                    <div className="text-xs text-yellow-500">Bekliyor</div>
                                  </div>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    )
                  )}

                  {/* Normal (tek kurum) görünümü veya çatı kurum seçili child detayı */}
                  {(!isParentInstitution || selectedChildId) && (
                  <>

                  {/* Özet kartları */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div className="bg-white rounded-2xl p-4 border border-gray-200 text-center">
                      <div className="text-2xl font-bold text-gray-900">{reconcSummary.total}</div>
                      <div className="text-xs text-gray-500 mt-1">Toplam Vaka</div>
                    </div>
                    <div className="bg-green-50 rounded-2xl p-4 border border-green-200 text-center">
                      <div className="text-2xl font-bold text-green-700">{reconcSummary.approved}</div>
                      <div className="text-xs text-green-600 mt-1">Mutabıkız</div>
                    </div>
                    <div className="bg-red-50 rounded-2xl p-4 border border-red-200 text-center">
                      <div className="text-2xl font-bold text-red-700">{reconcSummary.rejected}</div>
                      <div className="text-xs text-red-600 mt-1">Mutabık Değiliz</div>
                    </div>
                    <div className="bg-yellow-50 rounded-2xl p-4 border border-yellow-200 text-center">
                      <div className="text-2xl font-bold text-yellow-700">{reconcSummary.pending}</div>
                      <div className="text-xs text-yellow-600 mt-1">Bekliyor</div>
                    </div>
                  </div>

                  {/* Filtreler */}
                  <div className="flex flex-wrap gap-3 items-end bg-white/60 rounded-2xl p-4 border border-gray-200">
                    <div>
                      <Label className="text-xs text-gray-600 mb-1 block">Başlangıç Tarihi</Label>
                      <Input
                        type="date"
                        value={reconcStartDate}
                        onChange={(e) => { setReconcStartDate(e.target.value); setSelectedCaseIds([]); }}
                        className="rounded-xl text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-600 mb-1 block">Bitiş Tarihi</Label>
                      <Input
                        type="date"
                        value={reconcEndDate}
                        onChange={(e) => { setReconcEndDate(e.target.value); setSelectedCaseIds([]); }}
                        className="rounded-xl text-sm"
                      />
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      {['all', 'pending', 'approved', 'rejected'].map(f => (
                        <button
                          key={f}
                          onClick={() => { setReconcStatusFilter(f); setSelectedCaseIds([]); }}
                          className={`px-3 py-2 rounded-xl text-xs font-medium transition-all ${
                            reconcStatusFilter === f
                              ? 'bg-blue-600 text-white'
                              : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          {f === 'all' ? 'Tümü' : f === 'pending' ? 'Bekliyor' : f === 'approved' ? 'Mutabıkız' : 'Mutabık Değiliz'}
                        </button>
                      ))}
                    </div>
                    <div className="w-full border-t border-gray-100 pt-2 mt-1">
                      <span className="text-xs text-gray-500 font-medium mr-2">Durum:</span>
                      {[
                        { key: 'initiated', label: 'Başlatıldı' },
                        { key: 'in_progress', label: 'Devam Ediyor' },
                        { key: 'completed', label: 'Tamamlandı' },
                        { key: 'cancelled', label: 'İptal Edildi' },
                      ].map(({ key, label }) => (
                        <label key={key} className="inline-flex items-center gap-1 mr-4 cursor-pointer text-xs text-gray-600">
                          <input
                            type="checkbox"
                            checked={caseStatusFilter.includes(key)}
                            onChange={() => toggleCaseStatusFilter(key)}
                            className="rounded"
                          />
                          {label} ({caseStatusCounts[key]})
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Toplu aksiyon butonları */}
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="text-sm text-gray-600">{selectedCaseIds.length} vaka seçildi</span>
                    {canReconcile && filteredCases.length > 0 && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-green-500 text-green-700 hover:bg-green-50"
                          onClick={handleApprove}
                          disabled={selectedCaseIds.length === 0 || reconciliationMutation.isPending}
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Mutabıkız
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-red-500 text-red-700 hover:bg-red-50"
                          onClick={handleRejectOpen}
                          disabled={selectedCaseIds.length === 0 || reconciliationMutation.isPending}
                        >
                          <XCircle className="h-4 w-4 mr-1" />
                          Mutabık Değiliz
                        </Button>
                      </>
                    )}
                    {canSendEmail && !selectedChildId && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-blue-500 text-blue-700 hover:bg-blue-50 ml-auto"
                        onClick={() => setShowEmailModal(true)}
                      >
                        <Mail className="h-4 w-4 mr-1" />
                        E-posta Gönder
                      </Button>
                    )}
                    {institutionCases.length > 0 && (
                      <Button
                        size="sm"
                        variant="outline"
                        className={`border-emerald-500 text-emerald-700 hover:bg-emerald-50 ${canSendEmail && !selectedChildId ? '' : 'ml-auto'}`}
                        onClick={async () => {
                          try {
                            const params = new URLSearchParams();
                            if (reconcStartDate) params.append('startDate', reconcStartDate);
                            if (reconcEndDate) params.append('endDate', reconcEndDate);
                            const institutionId = isParentInstitution && selectedChildId ? selectedChildId : organization.id;
                            const response = await api.get(`/institutions/${institutionId}/reconciliation-excel?${params}`, { responseType: 'blob' });
                            const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `mutabakat_${(organization.name || 'kurum').replace(/[^a-z0-9]/gi, '_')}.xlsx`;
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                            URL.revokeObjectURL(url);
                            toast({ title: 'Başarılı', description: 'Mutabakat Excel indirildi' });
                          } catch {
                            toast({ title: 'Hata', description: 'Excel oluşturulurken hata oluştu', variant: 'destructive' });
                          }
                        }}
                      >
                        <FileText className="h-4 w-4 mr-1" />
                        Excel İndir
                      </Button>
                    )}
                    {institutionCases.length > 0 && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-indigo-500 text-indigo-700 hover:bg-indigo-50"
                        disabled={isDownloadingDocs}
                        onClick={async () => {
                          setIsDownloadingDocs(true);
                          toast({ title: 'Hazırlanıyor...', description: 'Tamamlama belgeleri toplanıyor, bu işlem birkaç dakika sürebilir' });
                          try {
                            const institutionId = isParentInstitution && selectedChildId ? selectedChildId : organization.id;
                            await downloadCompletionDocumentsZip('institutions', institutionId, organization.name, reconcStartDate, reconcEndDate);
                            toast({ title: 'Başarılı', description: 'Tamamlama belgeleri indirildi' });
                          } catch (err) {
                            toast({ title: 'Hata', description: err.message, variant: 'destructive' });
                          } finally {
                            setIsDownloadingDocs(false);
                          }
                        }}
                      >
                        <FileArchive className="h-4 w-4 mr-1" />
                        {isDownloadingDocs ? 'Hazırlanıyor...' : 'Tamamlama Belgeleri'}
                      </Button>
                    )}
                  </div>

                  {/* Vaka tablosu */}
                  {(reconcLoading || (isParentInstitution && parentCasesLoading)) ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                  ) : filteredCases.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <FileCheck className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>Bu kuruma ait vaka bulunamadı.</p>
                      <p className="text-sm mt-1">Tarih filtresi değiştirmeyi deneyin.</p>
                    </div>
                  ) : (
                    <div className="bg-white rounded-2xl border border-gray-200 overflow-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-gray-50">
                            {canReconcile && (
                              <th className="p-3 w-8">
                                <input
                                  type="checkbox"
                                  checked={filteredCases.length > 0 && filteredCases.every(c => selectedCaseIds.includes(c.id))}
                                  onChange={toggleSelectAll}
                                  className="rounded cursor-pointer"
                                />
                              </th>
                            )}
                            <th className="p-3 text-left font-medium text-gray-600">Vaka No</th>
                            <th className="p-3 text-left font-medium text-gray-600">Tarih</th>
                            <th className="p-3 text-left font-medium text-gray-600">Hasta</th>
                            <th className="p-3 text-left font-medium text-gray-600">Durum</th>
                            <th className="p-3 text-left font-medium text-gray-600">Mutabakat</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredCases.map((c) => {
                            const rBadge = getReconciliationBadge(c);
                            return (
                              <tr
                                key={c.id}
                                className={`border-b hover:bg-gray-50 ${selectedCaseIds.includes(c.id) ? 'bg-blue-50' : ''}`}
                              >
                                {canReconcile && (
                                  <td className="p-3">
                                    <input
                                      type="checkbox"
                                      checked={selectedCaseIds.includes(c.id)}
                                      onChange={() => toggleCaseSelect(c.id)}
                                      className="rounded cursor-pointer"
                                    />
                                  </td>
                                )}
                                <td className="p-3 font-mono text-xs text-blue-700 font-semibold">{c.case_number}</td>
                                <td className="p-3 text-xs text-gray-600">{c.request_date}</td>
                                <td className="p-3 text-xs">
                                  <span className="font-medium">{c.patient_name}</span>
                                  {c.patient_surname && <span className="text-gray-500"> {c.patient_surname}</span>}
                                </td>
                                <td className="p-3">
                                  <span className="inline-block px-2 py-1 rounded-lg text-xs bg-gray-100 text-gray-700">
                                    {c.status === 'initiated' ? 'Başlatıldı' : c.status === 'in_progress' ? 'Devam Ediyor' : c.status === 'completed' ? 'Tamamlandı' : 'İptal'}
                                  </span>
                                </td>
                                <td className="p-3">
                                  <div>
                                    <span className={`inline-block px-2 py-1 rounded-lg text-xs border ${rBadge.color}`}>
                                      {rBadge.label}
                                    </span>
                                    {c.reconciliation_note && (
                                      <p className="text-xs text-gray-500 mt-1 truncate max-w-[160px]" title={c.reconciliation_note}>
                                        ↳ {c.reconciliation_note}
                                      </p>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Gerekçe modali */}
                  {showRejectModal && createPortal(
                    <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4 overflow-y-auto">
                      <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl my-auto max-h-[90vh] overflow-y-auto">
                        <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2">
                          <XCircle className="w-5 h-5 text-red-500" />
                          Mutabık Değiliz — Gerekçe
                        </h3>
                        <p className="text-sm text-gray-600 mb-4">
                          <strong>{selectedCaseIds.length} vaka</strong> için "Mutabık Değiliz" gerekçesini açıklayın. Bu alan zorunludur.
                        </p>
                        <div className="space-y-2">
                          <Label>Gerekçe Açıklaması *</Label>
                          <Textarea
                            value={rejectNote}
                            onChange={(e) => setRejectNote(e.target.value)}
                            placeholder="Mutabık olmama gerekçenizi açıklayın..."
                            rows={4}
                            className="rounded-xl"
                          />
                        </div>
                        <div className="flex justify-end gap-2 mt-4">
                          <Button variant="outline" onClick={() => { setShowRejectModal(false); setRejectNote(''); }} className="rounded-xl">
                            İptal
                          </Button>
                          <Button
                            className="bg-red-600 hover:bg-red-700 text-white rounded-xl"
                            onClick={handleRejectConfirm}
                            disabled={!rejectNote.trim() || reconciliationMutation.isPending}
                          >
                            {reconciliationMutation.isPending ? 'Kaydediliyor...' : 'Reddet'}
                          </Button>
                        </div>
                      </div>
                    </div>,
                    document.body
                  )}

                  {/* E-posta Gönder modali (hastanelerdeki mutabakat mail sistemi ile aynı)
                      Portal ile body'ye taşınır: üst modaldeki backdrop-blur, `fixed`
                      konumlandırmayı kendine hapsedip modalı kırpıyordu. */}
                  {showEmailModal && createPortal(
                    <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4 overflow-y-auto">
                      <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl my-auto max-h-[90vh] overflow-y-auto">
                        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                          <Mail className="w-5 h-5 text-blue-600" />
                          E-posta Gönder — {organization?.name}
                        </h3>

                        <div className="space-y-4">
                          <div className="space-y-2">
                            <Label>E-posta Şablonu</Label>
                            <Select value={emailType} onValueChange={setEmailType}>
                              <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="reminder">🔔 Mutabakat Hatırlatması</SelectItem>
                                <SelectItem value="reconciliation_complete">✅ Mutabakat Onay Bildirimi</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="space-y-2">
                            <Label>Mutabakat Ayı</Label>
                            <Select value={emailMonth} onValueChange={setEmailMonth}>
                              <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                              <SelectContent className="max-h-64">
                                {monthOptions.map(opt => (
                                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {emailPeriod && (
                              <p className="text-xs text-gray-600">
                                Dönem: <span className="font-medium text-gray-800">{emailPeriod.rangeLabel}</span>
                                {' '}({emailPeriod.monthName} {emailPeriod.year} — {emailPeriod.lastDay} gün)
                              </p>
                            )}
                          </div>

                          <div>
                            <Label className="text-sm font-medium text-gray-700 mb-1 block">Alıcılar</Label>
                            {emailRecipient ? (
                              <div className="text-sm text-gray-700 py-0.5">
                                {emailRecipientName ? `${emailRecipientName} — ` : ''}
                                <span className="text-blue-700">{emailRecipient}</span>
                              </div>
                            ) : (
                              <p className="text-sm text-red-600">
                                Bu kurum için kayıtlı e-posta adresi yok. Önce "İletişim Bilgileri" sekmesinden ekleyin.
                              </p>
                            )}
                          </div>

                          <div className="p-3 bg-gray-50 rounded-xl text-xs text-gray-600">
                            {emailType === 'reminder'
                              ? `${emailPeriod ? `${emailPeriod.monthName} ${emailPeriod.year}` : 'Seçilen'} dönemi için mutabakat hatırlatma e-postası gönderilecek.`
                              : `${emailPeriod ? `${emailPeriod.monthName} ${emailPeriod.year}` : 'Seçilen'} dönemine ait bu kurumun vakaları Excel eki olarak mutabakat onay bildirimiyle birlikte gönderilecek.`}
                          </div>
                        </div>

                        <div className="flex justify-end gap-2 mt-6">
                          <Button variant="outline" onClick={() => setShowEmailModal(false)} className="rounded-xl">İptal</Button>
                          <Button onClick={handleSendEmail} disabled={isSendingEmail || !emailRecipient} className="rounded-xl">
                            {isSendingEmail ? 'Gönderiliyor...' : 'Gönder'}
                          </Button>
                        </div>
                      </div>
                    </div>,
                    document.body
                  )}
                  </>
                  )}
                </div>
              )}
            </div>

            {/* Modern Footer */}
            <div className="sticky bottom-0 z-40 bg-white/90 backdrop-blur-xl border-t border-gray-200/50 px-8 py-6 rounded-b-3xl">
              <div className="flex items-center justify-between">
                <Button
                  variant="outline"
                  onClick={onClose}
                  size="lg"
                  className="bg-white/60 border-0 hover:bg-white/80 rounded-2xl px-8"
                >
                  İptal
                </Button>
                {activeTab !== 'reconciliation' && (
                  <Button
                    onClick={handleSave}
                    disabled={saving}
                    size="lg"
                    className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 rounded-2xl px-8"
                  >
                    {saving ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        Kaydediliyor...
                      </>
                    ) : (
                      <>
                        <Save className="w-5 h-5 mr-2" />
                        Kaydet
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default OrganizationPricingModal; 